import { Question, Player, PlaceholderType } from '../types';

/**
 * Resolves template placeholders with actual player data
 */
export function resolvePlaceholders(
  template: string,
  placeholders: PlaceholderType[],
  players: Player[]
): { resolvedText: string; placeholdersResolved: Record<string, string> } {
  let resolvedText = template;
  const placeholdersResolved: Record<string, string> = {};
  
  // Get connected players only for resolution
  const connectedPlayers = players.filter(p => p.connected);
  
  console.log('🎭 Template resolution - available players:', {
    total: players.length,
    connected: connectedPlayers.length,
    players: connectedPlayers.map(p => ({ nickname: p.nickname, isHost: p.isHost })),
    template: template.slice(0, 50) + '...'
  });
  
  // Shuffle players for random selection
  const shuffledPlayers = [...connectedPlayers].sort(() => Math.random() - 0.5);
  
  for (let i = 0; i < placeholders.length; i++) {
    const placeholder = placeholders[i];
    
    if (placeholder.type === 'player') {
      // Select a player (cycling through if needed)
      const selectedPlayer = shuffledPlayers[i % shuffledPlayers.length];
      
      if (selectedPlayer) {
        // Replace placeholder with player nickname
        const placeholderPattern = new RegExp(`\\{\\{${placeholder.name}\\}\\}`, 'g');
        resolvedText = resolvedText.replace(placeholderPattern, selectedPlayer.nickname);
        
        console.log(`🎭 Placeholder ${placeholder.name} resolved to:`, {
          nickname: selectedPlayer.nickname,
          isHost: selectedPlayer.isHost,
          id: selectedPlayer.id
        });
        
        // Store the mapping
        placeholdersResolved[placeholder.name] = selectedPlayer.id;
      }
    }
  }
  
  return {
    resolvedText,
    placeholdersResolved
  };
}

/**
 * Checks if a question has enough players to be used
 */
export function validateMinPlayers(question: Question, playersCount: number): boolean {
  if (!question.minPlayers) return true;
  return playersCount >= question.minPlayers;
}

/**
 * Filters questions based on available players
 */
export function filterQuestionsForPlayers(questions: Question[], players: Player[]): Question[] {
  const connectedPlayers = players.filter(p => p.connected);
  
  console.log('🔍 filterQuestionsForPlayers called:', {
    totalQuestions: questions.length,
    connectedPlayers: connectedPlayers.length
  });
  
  const filtered = questions.filter(question => {
    // For non-template questions (static)
    if (!question.template || !question.placeholders || question.placeholders.length === 0) {
      const isActive = question.active;
      if (!isActive) {
        console.log('🚫 Static question filtered out (not active):', question.template?.slice(0, 50));
      }
      return isActive;
    }
    
    // For template questions, check minPlayers requirement
    const isActive = question.active;
    const hasMinPlayers = validateMinPlayers(question, connectedPlayers.length);
    
    if (!isActive) {
      console.log('🚫 Template question filtered out (not active):', question.template?.slice(0, 50));
    } else if (!hasMinPlayers) {
      console.log('🚫 Template question filtered out (not enough players):', {
        template: question.template?.slice(0, 50),
        minPlayers: question.minPlayers,
        currentPlayers: connectedPlayers.length
      });
    }
    
    return isActive && hasMinPlayers;
  });
  
  console.log('✅ filterQuestionsForPlayers result:', {
    input: questions.length,
    output: filtered.length,
    static: filtered.filter(q => !q.placeholders || q.placeholders.length === 0).length,
    templates: filtered.filter(q => q.placeholders && q.placeholders.length > 0).length
  });
  
  return filtered;
}

/**
 * Checks if a question is a template question
 */
export function isTemplateQuestion(question: Question): boolean {
  return !!(question.template && question.placeholders);
}

/**
 * Gets the display text for a question (resolved or original)
 */
export function getQuestionDisplayText(question: Question): string {
  return question.template || '';
}

/**
 * Filters questions based on category preference
 */
export function filterQuestionsByCategory(questions: Question[], category: string): Question[] {
  console.log('🏷️ filterQuestionsByCategory called:', { category, totalQuestions: questions.length });
  
  let filtered: Question[];
  
  if (category === 'all') {
    filtered = questions;
  } else if (category === 'static') {
    // Return only static questions (those without placeholders)
    filtered = questions.filter(q => !q.placeholders || q.placeholders.length === 0);
  } else if (category === 'templada' || category === 'templates') {
    // Return only template questions (those with placeholders)
    filtered = questions.filter(q => q.placeholders && q.placeholders.length > 0);
  } else {
    // Return questions that match the specific category + "mix" (universal wildcard)
    filtered = questions.filter(q => q.category === category || q.category === 'mix');
    
    // DETAILED DEBUGGING: Log categories in result for specific category selection
    if (category !== 'all' && category !== 'static' && category !== 'templada' && category !== 'templates') {
      const categoriesFound: Record<string, number> = {};
      filtered.forEach(q => {
        const qCat = q.category || 'undefined';
        categoriesFound[qCat] = (categoriesFound[qCat] || 0) + 1;
      });
      
      console.log(`🔍 CATEGORY FILTER DEBUG - Requested "${category}":`, {
        totalFiltered: filtered.length,
        categoriesInResult: categoriesFound,
        sampleQuestions: filtered.slice(0, 3).map(q => ({
          id: q.id,
          category: q.category,
          text: (q.template || 'No text').substring(0, 50) + '...'
        }))
      });
      
      // ALERT if we find questions from wrong categories
      const wrongCategories = Object.keys(categoriesFound).filter(cat => cat !== category);
      if (wrongCategories.length > 0) {
        console.error('🚨 CATEGORY BUG DETECTED!', {
          requested: category,
          foundWrongCategories: wrongCategories,
          wrongCount: wrongCategories.reduce((sum, cat) => sum + (categoriesFound[cat] || 0), 0)
        });
      }
    }
  }
  
  console.log('✅ filterQuestionsByCategory result:', {
    input: questions.length,
    output: filtered.length,
    category: category
  });
  
  return filtered;
}

/**
 * Creates a deterministic selection of players for placeholders
 * This ensures consistent results when the same players are available
 */
export function selectPlayersForPlaceholders(
  placeholders: PlaceholderType[],
  players: Player[]
): Record<string, Player> {
  const connectedPlayers = players.filter(p => p.connected);
  const selection: Record<string, Player> = {};
  
  // Sort players by ID for deterministic selection
  const sortedPlayers = [...connectedPlayers].sort((a, b) => a.id.localeCompare(b.id));
  
  for (let i = 0; i < placeholders.length; i++) {
    const placeholder = placeholders[i];
    
    if (placeholder.type === 'player' && sortedPlayers.length > 0) {
      // Use modulo to cycle through players if needed
      const selectedPlayer = sortedPlayers[i % sortedPlayers.length];
      selection[placeholder.name] = selectedPlayer;
    }
  }
  
  return selection;
}